import React, { useState } from 'react';
import {
  Search, Sparkles, Play, SkipBack, SkipForward, Volume2,
  Check, X, MessageSquareQuote, Music, AudioLines,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

/**
 * ⚠️ VISUAL MOCK — /booth-preview
 *
 * Renders the "Professional Booth" 6-Feature layout per /app/design_guidelines.json.
 * ZERO real data wiring. Hardcoded tracks / requests / playlists for design review.
 * Once approved, we wire this into ControlBoothPage.js with live data.
 */

const MOCK_NOW = {
  name: 'Midnight Blueprints',
  artist: 'Nova Palace',
  album: 'Chrome Nights EP',
  art: 'https://images.unsplash.com/photo-1744390166695-d797a8f913f1?w=600&q=80',
  bpm: 124,
  key: 'Am',
  progress: 47,
};

const MOCK_LIST = [
  { name: 'Midnight Blueprints', artist: 'Nova Palace', duration: '3:42', active: true },
  { name: 'Chrome Silhouette',  artist: 'Nova Palace', duration: '4:11' },
  { name: 'Halo Static',         artist: 'Aurora Kite', duration: '3:28' },
  { name: 'Neon Cartography',    artist: 'Yaan Wu',     duration: '5:02' },
  { name: 'Softly (Bass Mix)',   artist: 'Karan Aujla', duration: '3:19' },
  { name: 'Return of the Wave',  artist: 'Fascinated',  duration: '4:48' },
];

const MOCK_REQUESTS = [
  { type: 'song', guest: 'Priya',  track: 'Perfect — Ed Sheeran',          at: '2 min ago', unread: true },
  { type: 'shout', guest: 'Rahul', text: 'Happy Anniversary Neha ❤️',      at: '4 min ago', unread: true },
  { type: 'song', guest: 'Aisha',  track: 'Wake Me Up — Avicii',           at: '6 min ago', unread: true },
  { type: 'song', guest: 'Karan',  track: 'Blinding Lights — The Weeknd',  at: '11 min ago' },
];

const MOCK_PLAYLISTS = [
  { name: 'Sunset Cocktails', tracks: 42, bg: 'https://images.unsplash.com/photo-1762047314596-224a9c6e6261?w=600&q=80', active: true },
  { name: 'Peak Hour Afro',   tracks: 68, bg: 'https://images.unsplash.com/photo-1735623102107-561b6b24f355?w=600&q=80' },
  { name: 'Bollywood Nights', tracks: 55, bg: 'https://images.unsplash.com/photo-1744390166596-d1d0c0a76aa9?w=600&q=80' },
  { name: 'Deep House Slow',  tracks: 34, bg: 'https://images.unsplash.com/photo-1744390166695-d797a8f913f1?w=600&q=80' },
  { name: 'Sunday Chill',     tracks: 40, bg: 'https://images.unsplash.com/photo-1735623102107-561b6b24f355?w=600&q=80' },
];

const BoothPreview = () => {
  const [celebrateOpen, setCelebrateOpen] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#09090B] text-white overflow-hidden">

      {/* =========================================================
          MOBILE LAYOUT (block, hidden xl+)
          Order: Search+Celebrate → Compact Player → Queue → Picker
          Request Inbox intentionally hidden on mobile.
         ========================================================= */}
      <div className="xl:hidden flex flex-col min-h-screen">
        {/* Row 1 — Search + Celebrate icon */}
        <div className="flex items-center gap-2 p-3 border-b border-zinc-800/70">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Add a song to the queue…"
              className="pl-11 h-11 bg-zinc-900 border-zinc-800 rounded-full text-sm font-['Outfit'] focus:border-fuchsia-500"
              data-testid="mobile-search-input"
            />
          </div>
          <button
            onClick={() => setCelebrateOpen(true)}
            className="w-11 h-11 rounded-full bg-pink-500 hover:bg-pink-400 flex items-center justify-center shadow-[0_0_20px_rgba(236,72,153,0.45)] active:scale-95 transition-transform"
            data-testid="mobile-celebrate-btn"
            aria-label="Celebrate"
          >
            <Sparkles className="w-5 h-5 animate-pulse" />
          </button>
        </div>

        {/* Row 2 — Compact player (thin horizontal card) */}
        <div className="p-3 border-b border-zinc-800/70">
          <div className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-900 border border-zinc-800" data-testid="mobile-player-card">
            <img src={MOCK_NOW.art} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-bold truncate font-['Outfit']">{MOCK_NOW.name}</p>
              <p className="text-zinc-400 text-xs truncate">{MOCK_NOW.artist}</p>
              <div className="mt-1 h-0.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-fuchsia-500" style={{ width: `${MOCK_NOW.progress}%` }} />
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center active:scale-95 transition-transform">
                <SkipBack className="w-3.5 h-3.5" />
              </button>
              <button className="w-11 h-11 rounded-full bg-fuchsia-500 flex items-center justify-center shadow-[0_0_16px_rgba(217,70,239,0.5)] active:scale-95 transition-transform">
                <Play className="w-4 h-4 fill-current text-black ml-0.5" />
              </button>
              <button className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center active:scale-95 transition-transform">
                <SkipForward className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Row 3 — Queue (main scroll region) */}
        <div className="flex-1 overflow-y-auto" data-testid="mobile-queue-list">
          <div className="px-4 pt-3 pb-2 flex items-center justify-between">
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Up Next</span>
              <p className="text-sm font-bold text-white font-['Outfit']">Sunset Cocktails · <span className="text-zinc-500 font-mono">42</span></p>
            </div>
          </div>
          {MOCK_LIST.map((t, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-4 py-3 border-b border-zinc-800/40 ${t.active ? 'bg-fuchsia-500/5 border-l-4 border-l-fuchsia-500' : ''}`}
            >
              <span className="text-xs font-mono text-zinc-500 w-5">{String(i + 1).padStart(2, '0')}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold truncate font-['Outfit'] ${t.active ? 'text-fuchsia-400' : 'text-white'}`}>{t.name}</p>
                <p className="text-xs text-zinc-500 truncate">{t.artist}</p>
              </div>
              {t.active && <AudioLines className="w-4 h-4 text-fuchsia-400 animate-pulse" />}
              <span className="text-xs font-mono text-zinc-500">{t.duration}</span>
            </div>
          ))}
          <div className="h-24" /> {/* spacer so the last row isn't hidden by the collapsed picker */}
        </div>

        {/* Row 4 — Collapsible playlist picker (bottom-pinned) */}
        <div className="border-t border-zinc-800 bg-zinc-950" data-testid="mobile-picker">
          <button
            onClick={() => setPickerOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            data-testid="mobile-picker-toggle"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Music className="w-4 h-4 text-fuchsia-400" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Playlist</span>
              <span className="text-sm font-bold text-white truncate">Sunset Cocktails</span>
            </div>
            <span className={`text-zinc-400 transition-transform ${pickerOpen ? 'rotate-180' : ''}`}>⌄</span>
          </button>
          {pickerOpen && (
            <div className="px-4 pb-4 flex gap-3 overflow-x-auto hide-scrollbar">
              {MOCK_PLAYLISTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setPickerOpen(false)}
                  className={`relative flex-shrink-0 w-40 h-20 rounded-xl overflow-hidden border transition-all active:scale-95 ${
                    p.active ? 'border-fuchsia-500 shadow-[0_0_16px_rgba(217,70,239,0.35)]' : 'border-zinc-800'
                  }`}
                >
                  <img src={p.bg} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
                  <div className="absolute inset-0 p-2 flex flex-col justify-end text-left">
                    <p className="text-white font-bold font-['Outfit'] text-xs leading-tight">{p.name}</p>
                    <p className="text-zinc-400 text-[9px] font-mono">{p.tracks} tracks</p>
                  </div>
                  {p.active && <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-fuchsia-500 text-black text-[8px] font-bold tracking-wider">LIVE</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* =========================================================
          DESKTOP / TABLET LAYOUT (xl+, unchanged 12-col bento)
         ========================================================= */}
      <div className="hidden xl:grid xl:grid-cols-12 gap-4 p-4 h-screen">

        {/* ============ TOP BAR: Search + Celebration ============ */}
        <div className="xl:col-span-12 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              placeholder="Search or paste a link to queue a track…"
              className="pl-14 h-14 bg-zinc-900 border-zinc-800 rounded-full text-base font-['Outfit'] focus:border-fuchsia-500 focus:ring-fuchsia-500/20"
              data-testid="booth-search-input"
            />
          </div>
          <button
            onClick={() => setCelebrateOpen(true)}
            className="h-14 px-6 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold flex items-center gap-2 shadow-[0_0_30px_rgba(236,72,153,0.5)] hover:shadow-[0_0_40px_rgba(236,72,153,0.8)] hover:scale-[1.02] active:scale-95 transition-[transform,box-shadow]"
            data-testid="celebration-hub-btn"
          >
            <Sparkles className="w-5 h-5 animate-pulse" />
            <span className="tracking-wider">CELEBRATE</span>
          </button>
        </div>

        {/* ============ MIDDLE: Player | Song List | Inbox ============ */}
        {/* Master Player */}
        <section
          className="xl:col-span-3 bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col"
          data-testid="master-player-card"
        >
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Master Player</span>
            <span className="flex items-center gap-1 text-[10px] text-fuchsia-400 font-mono">
              <span className="w-1.5 h-1.5 bg-fuchsia-500 rounded-full animate-pulse" /> LIVE
            </span>
          </div>
          <div className="p-4 flex-1 flex flex-col">
            <div className="relative w-full aspect-square rounded-xl overflow-hidden border border-zinc-800 shadow-2xl">
              <img src={MOCK_NOW.art} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
            <div className="mt-4">
              <h2 className="text-xl font-black tracking-tight font-['Outfit'] truncate">{MOCK_NOW.name}</h2>
              <p className="text-zinc-400 text-sm truncate">{MOCK_NOW.artist} · {MOCK_NOW.album}</p>
            </div>
            <div className="mt-3 flex items-center gap-4 text-xs font-mono text-zinc-400">
              <span>BPM <span className="text-fuchsia-400">{MOCK_NOW.bpm}</span></span>
              <span>KEY <span className="text-fuchsia-400">{MOCK_NOW.key}</span></span>
            </div>
            <div className="mt-3">
              <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-fuchsia-500" style={{ width: `${MOCK_NOW.progress}%` }} />
              </div>
              <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
                <span>1:44</span><span>3:42</span>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center gap-3">
              <button className="w-11 h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors" data-testid="prev-btn">
                <SkipBack className="w-5 h-5" />
              </button>
              <button className="w-16 h-16 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 flex items-center justify-center shadow-[0_0_30px_rgba(217,70,239,0.5)] hover:scale-105 active:scale-95 transition-[transform,background-color]" data-testid="play-pause-btn">
                <Play className="w-7 h-7 fill-current text-black ml-0.5" />
              </button>
              <button className="w-11 h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors" data-testid="skip-btn">
                <SkipForward className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-zinc-500" />
              <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-cyan-400" style={{ width: '65%' }} />
              </div>
              <span className="text-[10px] font-mono text-zinc-500">65</span>
            </div>
          </div>
        </section>

        {/* Song List */}
        <section
          className="xl:col-span-5 bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col"
          data-testid="song-list-card"
        >
          <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Now Spinning</span>
              <h3 className="text-lg font-bold font-['Outfit']">Sunset Cocktails <span className="text-zinc-500 font-mono text-sm">· 42 tracks</span></h3>
            </div>
            <button className="text-xs px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 tracking-wider transition-colors">SHUFFLE</button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {MOCK_LIST.map((t, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 px-5 py-3 border-b border-zinc-800/50 transition-colors hover:bg-zinc-800/50 ${
                  t.active ? 'bg-fuchsia-500/5 border-l-4 border-l-fuchsia-500' : ''
                }`}
                data-testid={`songlist-row-${i}`}
              >
                <span className="text-xs font-mono text-zinc-500 w-6">{String(i + 1).padStart(2, '0')}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate font-['Outfit'] ${t.active ? 'text-fuchsia-400' : 'text-white'}`}>{t.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{t.artist}</p>
                </div>
                {t.active && <AudioLines className="w-4 h-4 text-fuchsia-400 animate-pulse" />}
                <span className="text-xs font-mono text-zinc-500 w-10 text-right">{t.duration}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Request Inbox */}
        <section
          className="xl:col-span-4 bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col"
          data-testid="request-inbox-card"
        >
          <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(6,182,212,0.8)]" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Live · 3 New</span>
            </div>
            <h3 className="text-lg font-bold font-['Outfit']">Requests</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {MOCK_REQUESTS.map((r, i) => (
              <div
                key={i}
                className={`p-3 rounded-xl border transition-colors ${
                  r.unread ? 'bg-zinc-800/60 border-zinc-700' : 'bg-zinc-900/60 border-zinc-800'
                }`}
                data-testid={`request-row-${i}`}
              >
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider mb-2">
                  {r.type === 'song' ? (
                    <><Music className="w-3 h-3 text-fuchsia-400" /><span className="text-fuchsia-400">Song</span></>
                  ) : (
                    <><MessageSquareQuote className="w-3 h-3 text-pink-400" /><span className="text-pink-400">Shoutout</span></>
                  )}
                  <span className="text-zinc-500 ml-auto">{r.at}</span>
                </div>
                <p className="text-sm font-semibold font-['Outfit'] truncate">{r.track || r.text}</p>
                <p className="text-xs text-zinc-500">from {r.guest}</p>
                <div className="mt-2 flex gap-2">
                  <button className="flex-1 h-8 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-bold flex items-center justify-center gap-1 transition-colors" data-testid={`request-approve-${i}`}>
                    <Check className="w-3.5 h-3.5" /> APPROVE
                  </button>
                  <button className="w-9 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors" data-testid={`request-reject-${i}`}>
                    <X className="w-4 h-4 text-zinc-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ============ BOTTOM: Vibe Picker ============ */}
        <div className="xl:col-span-12 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Today's Playlists</span>
            <span className="text-[10px] text-zinc-600 font-mono">← swipe →</span>
          </div>
          <div className="flex gap-3 overflow-x-auto hide-scrollbar snap-x">
            {MOCK_PLAYLISTS.map((p, i) => (
              <button
                key={i}
                className={`relative flex-shrink-0 w-48 h-24 rounded-2xl overflow-hidden border transition-all hover:scale-[1.02] active:scale-95 snap-start ${
                  p.active ? 'border-fuchsia-500 shadow-[0_0_20px_rgba(217,70,239,0.4)]' : 'border-zinc-800 hover:border-zinc-600'
                }`}
                data-testid={`vibe-tile-${i}`}
              >
                <img src={p.bg} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
                <div className="absolute inset-0 p-3 flex flex-col justify-end text-left">
                  <p className="text-white font-bold font-['Outfit'] text-sm leading-tight">{p.name}</p>
                  <p className="text-zinc-400 text-[10px] font-mono">{p.tracks} tracks</p>
                </div>
                {p.active && (
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-fuchsia-500 text-black text-[9px] font-bold tracking-wider">LIVE</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ============ Celebration Hub Dialog ============ */}
      <Dialog open={celebrateOpen} onOpenChange={setCelebrateOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl font-black">
              <Sparkles className="w-6 h-6 text-pink-400" />
              Celebration Hub
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-1 block">Guest Name</label>
              <Input
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="e.g. Priya"
                className="bg-zinc-800 border-zinc-700"
                data-testid="celebration-guest-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button className="h-16 bg-pink-500 hover:bg-pink-400 text-white font-bold text-lg flex flex-col gap-0" data-testid="celebration-birthday-btn">
                🎂 Birthday
              </Button>
              <Button className="h-16 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-bold text-lg flex flex-col gap-0" data-testid="celebration-congrats-btn">
                🎉 Congrats
              </Button>
            </div>
            <p className="text-xs text-zinc-500 text-center pt-1">
              Priority-1 injection · jumps the queue immediately
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCelebrateOpen(false)} className="text-zinc-400">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoothPreview;
